/** One column of a stored arrangement: where it stands and whether ticked. */
export interface ArrangedEntry {
    key: string;
    shown: boolean;
}

/** What an arrangement needs of a column on offer. */
export interface ArrangeableColumn {
    key: string;
    /** Ticked in the default order, and when new to a stored one. */
    preset: boolean;
    /**
     * Ticked by its own rule every time, never by what was stored — its place
     * is still remembered.
     */
    keepPreset?: boolean;
}

/**
 * The columns on offer in a stored order, each ticked or not. A column the
 * stored order does not name yet (one added since) goes in behind the column
 * it follows in the default order, ticked as there; a stored one no longer
 * on offer drops out. With nothing stored, that is the default order.
 */
export function arrangeColumns(
    columns: readonly ArrangeableColumn[],
    stored: readonly ArrangedEntry[]
): ArrangedEntry[] {
    const byKey = new Map(columns.map((column) => [column.key, column]));
    const arranged: ArrangedEntry[] = [];
    for (const entry of stored) {
        const column = byKey.get(entry.key);
        if (column && !arranged.some((placed) => placed.key === entry.key)) {
            arranged.push({
                key: entry.key,
                shown: column.keepPreset ? column.preset : entry.shown
            });
        }
    }
    columns.forEach((column, index) => {
        if (arranged.some((entry) => entry.key === column.key)) {
            return;
        }
        let at = 0;
        for (let i = index - 1; i >= 0; i--) {
            const before = arranged.findIndex(
                (entry) => entry.key === columns[i].key
            );
            if (before >= 0) {
                at = before + 1;
                break;
            }
        }
        arranged.splice(at, 0, { key: column.key, shown: column.preset });
    });
    return arranged;
}

/** The same columns in a new order, each keeping its tick. */
export function reorderedEntries(
    arranged: readonly ArrangedEntry[],
    keys: readonly string[]
): ArrangedEntry[] {
    const byKey = new Map(arranged.map((entry) => [entry.key, entry]));
    return keys
        .map((key) => byKey.get(key))
        .filter((entry): entry is ArrangedEntry => entry !== undefined);
}

/** The ticks of a picker laid onto the arrangement. */
export function withShown(
    arranged: readonly ArrangedEntry[],
    shown: ReadonlySet<string>
): ArrangedEntry[] {
    return arranged.map((entry) => ({
        key: entry.key,
        shown: shown.has(entry.key)
    }));
}
